import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Lock, Heart, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PLANS = {
  athlete_annual:   { label: "Athlete · Annual",  price: "$149 / year",   sub: "Save $79 vs monthly. Data persists across renewals.", color: "#22D3EE" },
  athlete_monthly:  { label: "Athlete · Monthly", price: "$19 / month",   sub: "Cancel any time during the off-season.", color: "#22D3EE" },
  // Legacy plans — kept selectable for existing customers / link-backs
  solo_annual:      { label: "Annual Pass (legacy)", price: "$39 / year",  sub: "Grandfathered legacy plan.", color: "#16A34A" },
  solo_monthly:     { label: "Monthly Flex (legacy)", price: "$4.99 / pm", sub: "Grandfathered legacy plan.", color: "#94A3B8" },
};
const PRIMARY_PLANS = ["athlete_annual", "athlete_monthly"];

export default function SoloSignup() {
  const [params, setParams] = useSearchParams();
  const initialPlan = params.get("plan") || "athlete_annual";
  const [plan, setPlan] = useState(PLANS[initialPlan] ? initialPlan : "athlete_annual");
  const [form, setForm] = useState({ name: "", email: "", password: "", parent_email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setParams({ plan }); }, [plan]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout/solo", {
        origin_url: window.location.origin,
        package_id: plan,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        parent_email: form.parent_email.trim() || null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const p = PLANS[plan];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="solo-signup-page">
      <header className="border-b border-[var(--ink-border)]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="md" variant="lockup" linkTo="/" dark />
          <Link to="/pricing" className="text-sm text-white/60 hover:text-white">All plans</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-10">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--ink-border)] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-bold text-white/70">
            <Sparkles className="h-3 w-3" style={{ color: "var(--brand-secondary)" }} /> Solo athlete · No team needed
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter text-white mt-5 leading-[0.95]">
            Your arm.<br />Your data.<br /><span style={{ color: p.color }}>One athlete account.</span>
          </h1>
          <p className="text-white/65 mt-5 leading-relaxed">
            Built for high-school, travel-ball, and college throwing athletes — pitchers, catchers, infielders, outfielders — who don&apos;t have a coach paying for the team tenant. You log daily, your Apple Watch or Pixel feeds in the rest, and the engine prescribes your throwing track.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-bold border"
            style={{ backgroundColor: "rgba(34,211,238,0.08)", borderColor: "rgba(34,211,238,0.35)", color: "#22D3EE" }}>
            <Sparkles className="h-3 w-3" /> Save $79 / year vs monthly
          </div>

          <div className="mt-8 space-y-3">
            {Object.entries(PLANS)
              .filter(([key]) => PRIMARY_PLANS.includes(key) || plan === key)
              .map(([key, val]) => (
              <button
                type="button"
                key={key}
                onClick={() => setPlan(key)}
                className={`w-full text-left rounded-xl p-4 border-2 transition-colors flex items-center gap-3 ${plan === key ? "border-[var(--brand-secondary)] bg-white/[0.04]" : "border-[var(--ink-border)] hover:bg-white/[0.02]"}`}
                data-testid={`plan-radio-${key}`}
              >
                <div className={`w-4 h-4 rounded-full border-2 ${plan === key ? "border-[var(--brand-secondary)]" : "border-white/30"}`}>
                  {plan === key && <div className="w-full h-full rounded-full" style={{ backgroundColor: "var(--brand-secondary)" }} />}
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-white">{val.label}</div>
                  <div className="text-xs text-white/50 mt-0.5">{val.sub}</div>
                </div>
                <div className="font-display font-bold text-white">{val.price}</div>
              </button>
            ))}
            <Link to="/legacy-pricing" className="block text-[11px] text-white/35 hover:text-white/60 mt-2 text-center tracking-[0.1em]" data-testid="solo-legacy-link">
              Existing customer on a legacy plan? See grandfathered options →
            </Link>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl ink-card p-7 self-start sticky top-6" data-testid="solo-signup-form">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: p.color }}>Step 1 · Create account</div>
          <h2 className="font-display font-black text-2xl text-white mt-1">{p.label}</h2>
          <p className="text-xs text-white/50 mt-1">{p.sub}</p>

          <div className="mt-5 space-y-4">
            <div>
              <Label className="text-white/70 text-xs">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Casey Pitcher" className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white mt-1.5" data-testid="solo-name" required />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Your email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white mt-1.5" data-testid="solo-email" required />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Password — 8+ characters</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white mt-1.5" data-testid="solo-password" required minLength={8} />
            </div>
            <div>
              <Label className="text-white/70 text-xs flex items-center gap-1.5">
                <Heart className="h-3.5 w-3.5" /> Parent email (optional)
              </Label>
              <Input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} placeholder="parent@email.com" className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white mt-1.5" data-testid="solo-parent-email" />
              <p className="text-[10px] text-white/40 mt-1">We&apos;ll cc this email on the Sunday Week-Ahead digest.</p>
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full mt-6 text-white font-bold rounded-full py-6" style={{ backgroundColor: p.color }} data-testid="solo-continue">
            {busy ? "Loading…" : <>Continue to checkout <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>

          <p className="text-[10px] text-white/40 mt-4 leading-relaxed">
            This is a deferred, subscription, or recurring purchase. By continuing, you agree to our <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline hover:text-white/70">Refund Policy</a> and <a href="/terms" target="_blank" rel="noreferrer" className="underline hover:text-white/70">Terms of Service</a>, and authorize ArmIQ to charge your payment method at the prices, frequency, and dates listed on this page until your order is fulfilled or you cancel, if permitted.
          </p>
          <p className="text-[10px] text-white/40 mt-2 inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Card details processed by our payment provider — they never touch our servers.
          </p>
        </form>
      </div>
    </div>
  );
}
